// The shape of /chatbot/settings, its defaults, and the one row widget the
// settings screen repeats. Kept beside the screen so the screen file stays a
// screen.

import { Toggle } from "../ui";
import type { MessageButton } from "./shared";

export interface BusinessHour {
  day: number;
  enabled: boolean;
  start_time: string;
  end_time: string;
}

/** Everything /chatbot/settings round-trips. The GET fills in what is set. */
export interface ChatbotSettingsForm {
  greeting_message: string;
  greeting_buttons: MessageButton[];
  fallback_message: string;
  fallback_buttons: MessageButton[];
  disclaimer_message: string;
  pii_blocked_message: string;
  nps_survey_message: string;
  nps_followup_detractor: string;
  nps_followup_passive: string;
  nps_followup_promoter: string;
  nps_thank_you_message: string;
  session_timeout_minutes: number;
  boxx_auth_enabled: boolean;
  boxx_auth_available: boolean;

  allow_agent_queue_pickup: boolean;
  assign_to_same_agent: boolean;
  agent_current_conversation_only: boolean;

  business_hours_enabled: boolean;
  business_hours: BusinessHour[];
  out_of_hours_message: string;
  allow_automated_outside_hours: boolean;

  sla_enabled: boolean;
  sla_response_minutes: number;
  sla_resolution_minutes: number;
  sla_escalation_minutes: number;
  sla_auto_close_hours: number;
  sla_auto_close_message: string;
  sla_warning_message: string;
  sla_escalation_notify_ids: string[];
  client_reminder_enabled: boolean;
  client_reminder_minutes: number;
  client_reminder_message: string;
  client_auto_close_minutes: number;
  client_auto_close_message: string;

  ai_enabled: boolean;
  ai_provider: string;
  ai_model: string;
  ai_max_tokens: number;
}

/** The API key is write-only: the GET never returns it. */
export interface SettingsUpdate extends Partial<ChatbotSettingsForm> {
  pii_detection_enabled?: boolean;
  pii_log_detections?: boolean;
  ai_api_key?: string;
}

export interface SettingsGetResponse {
  settings?: Partial<ChatbotSettingsForm>;
}

export interface BackfillResponse {
  message?: string;
  flow_count?: number;
  lang?: string;
}

const DEFAULT_DISCLAIMER =
  "👋 Welcome to BOXX Cyber Concierge! I'm here to help you spot scams, recover from fraud, and stay protected online.\n\n⚠️ *Disclaimer:* This is an AI-powered assistant. The information provided is for general guidance only and does not constitute legal, financial, or law enforcement advice. Do not share sensitive personal information such as passwords, OTPs, or bank account details in this chat. If you are in immediate financial risk, please contact your bank or local authorities without delay.\n\nPlease tap *I Agree* to continue.";

const DEFAULT_NPS_SURVEY =
  "Your experience matters — not just to us, but to everyone who may need this support in the future. We'd love your feedback. Based on the support you received, how likely are you to recommend this service to others?";
const DEFAULT_NPS_FU_DETRACTOR =
  "We're sorry we couldn't meet your expectations. Could you tell us what went wrong? Your feedback will help us improve.\n\nPlease select the reason that best describes your experience:\n\n*1.* Response was not relevant to my issue\n*2.* The issue was not resolved\n*3.* Information was unclear or hard to follow\n*4.* The process felt too long\n*5.* Other feedback";
const DEFAULT_NPS_FU_PASSIVE =
  "Thank you - glad we could help to some extent. What could have made this experience better for you?\n\nPlease select the reason that best describes your experience:\n\n*1.* Response was helpful but issue partially unresolved\n*2.* Information was clear but needed more guidance\n*3.* The process was a bit long\n*4.* I needed to speak to a real person\n*5.* Other feedback";
const DEFAULT_NPS_FU_PROMOTER =
  "That means a lot — thank you! Is there anything specific that stood out or that we could still do better?\n\nPlease select the reason that best describes your experience:\n\n*1.* Response was very helpful and clear\n*2.* The issue was fully resolved\n*3.* Process was smooth and easy to follow\n*4.* Everything was great\n*5.* Other feedback";
const DEFAULT_NPS_THANKYOU =
  "Thank you for sharing. Your feedback helps us improve support for everyone. Stay safe.";

export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const DEFAULT_HOURS: BusinessHour[] = [
  { day: 0, enabled: false, start_time: "09:00", end_time: "17:00" },
  { day: 1, enabled: true, start_time: "09:00", end_time: "17:00" },
  { day: 2, enabled: true, start_time: "09:00", end_time: "17:00" },
  { day: 3, enabled: true, start_time: "09:00", end_time: "17:00" },
  { day: 4, enabled: true, start_time: "09:00", end_time: "17:00" },
  { day: 5, enabled: true, start_time: "09:00", end_time: "17:00" },
  { day: 6, enabled: false, start_time: "09:00", end_time: "17:00" },
];

export const AI_PROVIDERS: { value: string; label: string; models: string[] }[] = [
  { value: "openai", label: "OpenAI", models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"] },
  {
    value: "anthropic",
    label: "Anthropic",
    models: ["claude-3-5-sonnet-latest", "claude-3-5-haiku-latest", "claude-3-opus-latest"],
  },
  {
    value: "google",
    label: "Google AI",
    models: [
      "gemini-2.5-flash",
      "gemini-3.0-flash",
      "gemini-2.0-flash",
      "gemini-2.0-flash-lite",
      "gemini-1.5-flash",
      "gemini-1.5-flash-8b",
    ],
  },
];

export const PII_PATTERNS = [
  "Card number",
  "Bank account",
  "IFSC code",
  "CVV",
  "Aadhaar",
  "OTP",
  "PAN",
  "Driving licence",
  "Passport",
  "Voter ID",
  "Long digit run",
];

/** Only this operator sees the BOXX auth probe — it calls a partner API for real. */
export const BOXX_AUTH_TEST_EMAIL = "ankit@shunyalabs.ai";

export function normaliseSettings(raw: Partial<ChatbotSettingsForm> | undefined): ChatbotSettingsForm {
  const s = raw ?? {};
  // Every day is present even when the server stored a subset, so the editor
  // never silently drops a day the operator cannot see.
  const loaded = s.business_hours ?? [];
  const hours = DEFAULT_HOURS.map((d) => loaded.find((h) => h.day === d.day) ?? d);
  return {
    greeting_message: s.greeting_message ?? "",
    greeting_buttons: s.greeting_buttons ?? [],
    fallback_message: s.fallback_message ?? "",
    fallback_buttons: s.fallback_buttons ?? [],
    disclaimer_message: s.disclaimer_message || DEFAULT_DISCLAIMER,
    pii_blocked_message: s.pii_blocked_message ?? "",
    nps_survey_message: s.nps_survey_message || DEFAULT_NPS_SURVEY,
    nps_followup_detractor: s.nps_followup_detractor || DEFAULT_NPS_FU_DETRACTOR,
    nps_followup_passive: s.nps_followup_passive || DEFAULT_NPS_FU_PASSIVE,
    nps_followup_promoter: s.nps_followup_promoter || DEFAULT_NPS_FU_PROMOTER,
    nps_thank_you_message: s.nps_thank_you_message || DEFAULT_NPS_THANKYOU,
    session_timeout_minutes: s.session_timeout_minutes || 1440,
    boxx_auth_enabled: s.boxx_auth_enabled === true,
    boxx_auth_available: s.boxx_auth_available === true,

    allow_agent_queue_pickup: s.allow_agent_queue_pickup !== false,
    assign_to_same_agent: s.assign_to_same_agent !== false,
    agent_current_conversation_only: s.agent_current_conversation_only === true,

    business_hours_enabled: s.business_hours_enabled === true,
    business_hours: hours,
    out_of_hours_message: s.out_of_hours_message ?? "",
    allow_automated_outside_hours: s.allow_automated_outside_hours !== false,

    sla_enabled: s.sla_enabled === true,
    sla_response_minutes: s.sla_response_minutes || 15,
    sla_resolution_minutes: s.sla_resolution_minutes || 60,
    sla_escalation_minutes: s.sla_escalation_minutes || 30,
    sla_auto_close_hours: s.sla_auto_close_hours || 24,
    sla_auto_close_message: s.sla_auto_close_message ?? "",
    sla_warning_message: s.sla_warning_message ?? "",
    sla_escalation_notify_ids: s.sla_escalation_notify_ids ?? [],
    client_reminder_enabled: s.client_reminder_enabled === true,
    client_reminder_minutes: s.client_reminder_minutes || 30,
    client_reminder_message: s.client_reminder_message ?? "",
    client_auto_close_minutes: s.client_auto_close_minutes || 60,
    client_auto_close_message: s.client_auto_close_message ?? "",

    ai_enabled: s.ai_enabled === true,
    ai_provider: s.ai_provider ?? "",
    ai_model: s.ai_model ?? "",
    ai_max_tokens: s.ai_max_tokens || 500,
  };
}

/** A labelled row with a switch on the right — the shape every toggle uses here. */
export function SettingRow({
  title,
  hint,
  checked,
  onChange,
}: {
  title: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="setting">
      <div className="text">
        <div className="strong">{title}</div>
        {hint && <p>{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

export const SETTINGS_TABS = [
  { id: "messages", label: "Messages" },
  { id: "agents", label: "Agents" },
  { id: "hours", label: "Hours" },
  { id: "sla", label: "SLA" },
  { id: "ai", label: "AI" },
  { id: "knowledge", label: "Knowledge base" },
];
